import { formatStageHistoryDateTime } from "../../lib/format";
import { Clock, RotateCcw, ArrowRight, CheckCircle2, Activity, User } from "lucide-react";

export default function OrderHistoryTrail({ history = [], limit = null, showTitle = true }) {
  if (!history || history.length === 0) {
    return (
      <div style={{ color: "#94a3b8", fontSize: 12.5, padding: "12px 0", textAlign: "center" }}>
        No stage history recorded yet.
      </div>
    );
  }

  // Reverse so newest entries appear at the top
  const displayedHistory = [...history].reverse();
  const items = limit ? displayedHistory.slice(0, limit) : displayedHistory;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="order-history-trail">
      {items.map((entry, idx) => {
        const isBackward =
          entry.is_backward ||
          entry.action === "move_back" ||
          (entry.event && entry.event.toLowerCase().includes("moved back")) ||
          (entry.from_stage && entry.to_stage);

        const fromStage = entry.from_stage || (entry.event?.includes("→") ? entry.event.split("→")[0].replace("Moved back:", "").trim() : "");
        const toStage = entry.to_stage || (entry.event?.includes("→") ? entry.event.split("→")[1].trim() : "");
        const reasonText = entry.reason || (entry.remarks?.startsWith("Reason:") ? entry.remarks.replace(/^Reason:\s*/i, "") : entry.remarks);
        const dateFormatted = formatStageHistoryDateTime(entry.at);

        if (isBackward) {
          return (
            <div
              key={idx}
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 6,
                padding: "9px 12px",
                fontSize: 12.5,
              }}
              data-testid={`history-entry-backward-${idx}`}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      background: "#fef3c7",
                      color: "#b45309",
                      padding: "1px 6px",
                      borderRadius: 4,
                      fontSize: 10.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    <RotateCcw size={10} /> Moved Back
                  </span>
                  <span style={{ fontWeight: 600, color: "#92400e", fontSize: 12.5 }}>
                    {fromStage || "Stage"} <span style={{ color: "#d97706" }}>→</span> {toStage || "Previous Stage"}
                  </span>
                </div>
                <span style={{ color: "#92400e", fontSize: 11, fontWeight: 500 }}>
                  {dateFormatted}
                </span>
              </div>

              {reasonText && (
                <div
                  style={{
                    marginTop: 5,
                    color: "#78350f",
                    fontSize: 12,
                    lineHeight: 1.4,
                    background: "rgba(255, 255, 255, 0.75)",
                    border: "1px solid #fef3c7",
                    borderRadius: 4,
                    padding: "4px 8px",
                  }}
                  data-testid={`history-reason-${idx}`}
                >
                  <b style={{ color: "#92400e" }}>Reason: </b>
                  {reasonText}
                </div>
              )}

              <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#b45309" }}>
                <User size={11} />
                <span>Moved back by: <b style={{ color: "#78350f" }}>{entry.by || "System"}</b></span>
              </div>
            </div>
          );
        }

        // Standard Forward or Milestone Entry
        return (
          <div
            key={idx}
            style={{
              display: "flex",
              gap: 9,
              fontSize: 12,
              borderBottom: idx === items.length - 1 ? "none" : "1px solid #f1f5f9",
              paddingBottom: 7,
              paddingTop: idx === 0 ? 0 : 3,
            }}
            data-testid={`history-entry-standard-${idx}`}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#f1f5f9",
                color: "#0284c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              <Activity size={10.5} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                <b style={{ color: "var(--navy)", fontSize: 12.5, fontWeight: 600 }}>{entry.event}</b>
                <span style={{ color: "#64748b", fontSize: 11 }}>
                  {dateFormatted}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#64748b", marginTop: 1 }}>
                <span>by <b style={{ color: "#334155", fontWeight: 500 }}>{entry.by || "System"}</b></span>
              </div>

              {entry.remarks && (
                <p
                  style={{
                    margin: "3px 0 0",
                    color: "#475569",
                    fontSize: 11.5,
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                  }}
                >
                  {entry.remarks}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
